import { inject, Injectable } from '@angular/core';
import { DialogHelper } from '@common/modules/dialog/dialog.helper';
import { RecordsSandbox } from '@common/modules/records/sandbox/records.sandbox.service';
import { ActionDisabledCondition, ActionItemOptions, UserAction } from '@mediahaven/shared';
import {
    ApiFragment,
    Family,
    FragmentId,
    Mh2Entity,
    NavigationLink,
    RecordId,
    RecordTypesService,
    RecordTypeTranslationService
} from '@stylehaven/components';
import { EMPTY, Observable, of } from 'rxjs';
import { mapTo, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { MediaHavenActionType } from '../../../entities/action-types.entity';
import { ActionService } from '../../abstract/action.service.interface';

@Injectable({ providedIn: 'root' })
export class DecoupleFromParentActionService extends ActionService {
    protected readonly action: MediaHavenActionType = MediaHavenActionType.DECOUPLE_FROM_PARENT;

    // Services
    private readonly recordsSandbox: RecordsSandbox = inject(RecordsSandbox);
    private readonly recordTypeService: RecordTypesService = inject(RecordTypesService);
    private readonly recordTypeTranslationService: RecordTypeTranslationService = inject(RecordTypeTranslationService);
    private readonly dialogHelper: DialogHelper = inject(DialogHelper);

    // Behaviour
    protected readonly ENABLE_BUSY_TOAST: boolean = false;
    protected readonly REFRESH_ON_ACTION: boolean = true;
    protected readonly DISABLE_ON_EXECUTE: boolean = true;

    /* Decouple from parent
     * Available when:
     *  * User has edit rights
     *  * Has parent
     *  * Record type can be top-level
     *
     * Disabled when:
     *  * Has non-editable status
     */
    public getUserAction(record: ApiFragment): Observable<UserAction> {
        return of(this.userActionBuilder(record));
    }

    public userActionBuilder(record: ApiFragment): UserAction {
        const hasEditRights: boolean = record.hasEditRights();
        const hasParent: boolean = record.hasParent() || record.isCandidateChild();
        const isDeleted: boolean = record.isDeleted();
        const isTopLevel: boolean = this.recordTypeService.isTopLevelType(record.getRecordType());

        const hasEditableStatus: boolean = record.get(Family.Context, 'HasEditableStatus');

        const availableConditions: boolean[] = [hasEditRights, hasParent, !isDeleted, isTopLevel];
        const disableConditions: ActionDisabledCondition[] = [
            { condition: !hasEditableStatus, tooltip: 'general.blocked-status' }
        ];

        return new UserAction(availableConditions, disableConditions);
    }

    protected onAction(target: ApiFragment, actionItemOptions: ActionItemOptions): Observable<boolean> {
        const parentId: FragmentId | RecordId | null = target.getParentRecordId() ?? target.getCandidateParentId();

        if (!parentId) {
            console.error('DecoupleFromParentActionService:: Parent Id not found');
            return of(false);
        }
        const parent$: Observable<ApiFragment> = this.recordsSandbox.getRecord(parentId).pipe(shareReplay(1));

        return parent$.pipe(
            switchMap(parent => this.removeRecordFromParent(target, parent, actionItemOptions)),
            take(1)
        );
    }

    private removeRecordFromParent(
        target: ApiFragment,
        parent: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): Observable<boolean> {
        actionItemOptions.parent = parent;

        const title: string = this.translate('actions.intellectual.remove');
        const yes: string = title;
        const no: string = this.translate('actions.common.cancel');
        const message: string = this.getDecoupleFromParentConfirmationMessage(target.getMh2Entity(), parent);

        return this.dialogHelper.confirm({ title, message, yes, no }).pipe(
            tap(() => this.initBusyToast(target, actionItemOptions, true)),
            switchMap(confirm => (confirm ? this.recordsSandbox.removeRecordFromParent(target, parent) : EMPTY)),
            mapTo(true)
        );
    }

    private getDecoupleFromParentConfirmationMessage(sourceEntity: Mh2Entity, parent: ApiFragment): string {
        return this.translate(`items.delete-confirm-dialog.from-intellectual.${sourceEntity}`, {
            items: 1,
            title: parent.getTitle(),
            parentType: this.recordTypeTranslationService.getRecordTypeTranslation(parent.getRecordType())
        });
    }

    public getLabelKey(target: ApiFragment): string {
        return 'actions.intellectual.remove';
    }

    public getToastLabelKey(target: ApiFragment): string {
        return 'action-toasts.remove-from-intellectual';
    }

    public getToastEntity(target: ApiFragment, actionItemOptions: ActionItemOptions): Mh2Entity | string {
        return target.getMh2Entity();
    }

    public getSuccessToastLink(target: ApiFragment, actionItemOptions: ActionItemOptions): NavigationLink | undefined {
        return undefined;
    }

    public getSuccessToastLinkLabelKey(target: ApiFragment, actionItemOptions: ActionItemOptions): string {
        return '';
    }

    public getToastTranslationOptions(
        target: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): Record<string, string> {
        return {
            parentType: this.recordTypeTranslationService.getRecordTypeTranslation(
                actionItemOptions.parent!.getRecordType()
            )
        };
    }
}
